import {useDisclose} from "@hosttools/frontend";
import type {Listing} from "@hosttools/frontend/models/listing";
import {useCallback, useRef, useState} from "react";
import type {FieldArray} from "react-hook-form";
import {useFieldArray, useFormContext} from "react-hook-form";

function useHandleArrayField<
    T extends "taxes" | "fees" | "nearbyAmenities" | "attributesWithQuantity",
    V extends NonNullable<Listing[T]>[number]
>(name: T) {
    const {control} = useFormContext<Listing>();
    const {fields, append, update, remove} = useFieldArray<Listing, T, "_id">({
        control,
        name
    });
    const [obj, setObj] = useState<V | undefined>(undefined);
    const {isOpen, onOpen, onClose} = useDisclose();
    const idxRef = useRef<number>(-1);

    const onEdit = useCallback(
        (tax: V, idx?: number) => {
            setObj(tax);
            onOpen();
            if (typeof idx === "number") {
                idxRef.current = idx;
            }
        },
        [onOpen]
    );

    const onCancel = useCallback(() => {
        setObj(undefined);
        idxRef.current = -1;
        onClose();
    }, [onClose]);

    const onSave = useCallback(
        (item: V) => {
            const idx = idxRef.current;
            if (idx > -1) {
                // this doesn't look nice but there is no better way
                update(idx, item as FieldArray<Listing, T>);
            } else {
                append(item as FieldArray<Listing, T>);
            }
            onClose();
            setObj(undefined);
            idxRef.current = -1;
        },
        [update, append, onClose]
    );

    return {
        isOpen,
        obj,
        fields,
        index: idxRef.current,
        onOpen,
        onClose,
        onEdit,
        onRemove: remove,
        onSave,
        onCancel
    };
}

export default useHandleArrayField;
